import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { fmtDate as _fmtDate, humanizeDates } from '@/lib/dates';
import { toast } from 'sonner';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import {
  LayoutGrid, CalendarDays, MoreHorizontal, UserCog, CalendarClock, Truck, PlayCircle,
  ChevronLeft, ChevronRight, MapPin, Building2, BellRing,
} from 'lucide-react';
import { AssignDialog, ScheduleDialog } from './DispatchDialogs';

const CLOSED = ['Cancelled', 'Closed by Admin', 'Closed', 'Completed', 'Work Completed'];
const TO_SCHEDULE = ['Awaiting Work Order Processing', 'Open - Unassigned', 'Assigned', 'Open'];

const COLUMNS = [
  { key: 'to_schedule', label: 'To Schedule', accent: '#0E2A47', match: (w) => TO_SCHEDULE.includes(w.status) },
  { key: 'scheduled', label: 'Scheduled', accent: '#2F6DB5', match: (w) => w.status === 'Scheduled' },
  { key: 'dispatched', label: 'Dispatched', accent: '#C8641E', match: (w) => ['Dispatched', 'En Route'].includes(w.status) },
  { key: 'in_progress', label: 'In Progress', accent: '#5FA718', match: (w) => w.status === 'In Progress' },
];

const PRIORITY_COLOR = { P1: 'bg-red-100 text-red-700', P2: 'bg-orange-100 text-orange-700', P3: 'bg-amber-100 text-amber-700', P4: 'bg-sky-100 text-sky-700', P5: 'bg-slate-100 text-slate-600' };

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const fmtDate = (d) => { if (!d) return null; return _fmtDate(d) || d; };

const WoCard = ({ wo, onOpen, onAssign, onSchedule, onDispatch, onStart }) => {
  const assignee = wo.assigned_to_name || null;
  const canDispatch = (wo.assigned_to || wo.service_provider_id) && ['Scheduled', 'Assigned'].includes(wo.status);
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm hover:shadow-md transition-shadow"
      data-testid={`dispatch-card-${wo.tfs_work_order_number}`}>
      <div className="flex items-start justify-between gap-2">
        <button onClick={() => onOpen(wo.id)} className="text-left" data-testid={`dispatch-open-${wo.tfs_work_order_number}`}>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[11px] font-semibold text-[#0E2A47]">{wo.tfs_work_order_number}</span>
            {wo.priority_level && <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${PRIORITY_COLOR[wo.priority_level] || 'bg-slate-100 text-slate-600'}`}>{wo.priority_level}</span>}
          </div>
          <p className="mt-1 text-sm font-medium text-slate-800 line-clamp-2">{humanizeDates(wo.job_name) || '—'}</p>
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="rounded-md p-1 hover:bg-gray-100" data-testid={`dispatch-menu-${wo.tfs_work_order_number}`}>
              <MoreHorizontal size={16} className="text-slate-500" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onOpen(wo.id)}>Open Work Order</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAssign(wo)} data-testid={`dispatch-assign-${wo.tfs_work_order_number}`}><UserCog size={14} className="mr-2" /> Assign</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onSchedule(wo)} data-testid={`dispatch-schedule-${wo.tfs_work_order_number}`}><CalendarClock size={14} className="mr-2" /> Schedule</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onDispatch(wo)} data-testid={`dispatch-dispatch-${wo.tfs_work_order_number}`}><Truck size={14} className="mr-2" /> Dispatch</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onStart(wo)} data-testid={`dispatch-start-${wo.tfs_work_order_number}`}><PlayCircle size={14} className="mr-2" /> Mark In Progress</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="mt-2 space-y-1 text-xs text-slate-500">
        <div className="flex items-center gap-1"><Building2 size={12} /> {wo.customer_name || '—'}</div>
        <div className="flex items-center gap-1"><MapPin size={12} /> <span className="truncate">{wo.service_location || '—'}</span></div>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <span className={`text-[11px] font-medium ${assignee ? 'text-[#0E2A47]' : 'text-slate-400'}`}>{assignee || 'Unassigned'}</span>
        {wo.scheduled_date && <span className="text-[11px] font-semibold text-[#2F6DB5]">{fmtDate((wo.scheduled_date || '').slice(0, 10))}</span>}
      </div>
    </div>
  );
};

const BoardView = ({ items, actions }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="dispatch-board">
    {COLUMNS.map((col) => {
      const cards = items.filter(col.match);
      return (
        <div key={col.key} className="flex flex-col rounded-xl bg-gray-50/70 border border-gray-100" data-testid={`dispatch-col-${col.key}`}>
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: col.accent }} />
              <span className="text-sm font-bold text-[#0E2A47]">{col.label}</span>
            </div>
            <span className="rounded-full bg-white border border-gray-200 px-2 py-0.5 text-xs font-semibold text-slate-500">{cards.length}</span>
          </div>
          <div className="flex-1 space-y-3 p-3 min-h-[120px] max-h-[68vh] overflow-y-auto">
            {cards.map((w) => <WoCard key={w.id} wo={w} {...actions} />)}
            {cards.length === 0 && <p className="py-8 text-center text-xs text-slate-400">Nothing here.</p>}
          </div>
        </div>
      );
    })}
  </div>
);

const startOfWeek = (d) => { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); x.setHours(0, 0, 0, 0); return x; };
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const CalendarView = ({ items, reminders = [], mode, cursor, setCursor, onOpen, onOpenReminder }) => {
  const scheduled = useMemo(() => {
    const map = {};
    items.forEach((w) => {
      const d = (w.scheduled_date || '').slice(0, 10);
      if (d) { (map[d] = map[d] || []).push(w); }
    });
    return map;
  }, [items]);

  const followUps = useMemo(() => {
    const map = {};
    reminders.forEach((r) => {
      const d = (r.follow_up_date || '').slice(0, 10);
      if (d) { (map[d] = map[d] || []).push(r); }
    });
    return map;
  }, [reminders]);

  const days = useMemo(() => {
    if (mode === 'week') {
      const s = startOfWeek(cursor);
      return Array.from({ length: 7 }, (_, i) => { const d = new Date(s); d.setDate(s.getDate() + i); return d; });
    }
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const gridStart = startOfWeek(first);
    const total = 42;
    return Array.from({ length: total }, (_, i) => { const d = new Date(gridStart); d.setDate(gridStart.getDate() + i); return d; });
  }, [cursor, mode]);

  const todayIso = iso(new Date());
  const title = mode === 'week'
    ? `Week of ${_fmtDate(startOfWeek(cursor))}`
    : `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
  const shift = (dir) => {
    const d = new Date(cursor);
    if (mode === 'week') d.setDate(d.getDate() + dir * 7); else d.setMonth(d.getMonth() + dir);
    setCursor(d);
  };

  return (
    <div data-testid="dispatch-calendar">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} className="rounded-md border border-gray-200 p-1.5 hover:bg-gray-50" data-testid="cal-prev"><ChevronLeft size={16} /></button>
          <button onClick={() => setCursor(new Date())} className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium hover:bg-gray-50" data-testid="cal-today">Today</button>
          <button onClick={() => shift(1)} className="rounded-md border border-gray-200 p-1.5 hover:bg-gray-50" data-testid="cal-next"><ChevronRight size={16} /></button>
        </div>
        <h3 className="text-sm font-bold text-[#0E2A47]" data-testid="cal-title">{title}</h3>
      </div>
      <div className="grid grid-cols-7 gap-px rounded-lg overflow-hidden border border-gray-200 bg-gray-200">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} className="bg-gray-50 py-2 text-center text-[11px] font-bold uppercase tracking-wide text-slate-500">{d}</div>
        ))}
        {days.map((d, i) => {
          const key = iso(d);
          const inMonth = mode === 'week' || d.getMonth() === cursor.getMonth();
          const list = scheduled[key] || [];
          const fups = followUps[key] || [];
          return (
            <div key={i} className={`bg-white p-1.5 ${mode === 'week' ? 'min-h-[240px]' : 'min-h-[104px]'} ${inMonth ? '' : 'bg-gray-50/60'}`} data-testid={`cal-day-${key}`}>
              <div className={`text-[11px] font-semibold mb-1 ${key === todayIso ? 'text-white bg-[#0E2A47] rounded-full w-5 h-5 flex items-center justify-center' : inMonth ? 'text-slate-600' : 'text-slate-300'}`}>{d.getDate()}</div>
              <div className="space-y-1">
                {list.map((w) => (
                  <button key={w.id} onClick={() => onOpen(w.id)} data-testid={`cal-wo-${w.tfs_work_order_number}`}
                    className="block w-full text-left rounded bg-[#EAF0F8] hover:bg-[#dbe6f5] px-1.5 py-1 text-[10px] leading-tight">
                    <span className="font-mono font-semibold text-[#0E2A47]">{w.tfs_work_order_number}</span>
                    <span className="block truncate text-slate-600">{humanizeDates(w.job_name)}</span>
                  </button>
                ))}
                {fups.map((r) => (
                  <button key={r.id} onClick={() => onOpenReminder && onOpenReminder(r)} data-testid={`cal-followup-${r.tfs_work_order_number}`}
                    title={r.recommendation}
                    className="flex w-full items-center gap-1 rounded bg-amber-100 hover:bg-amber-200 px-1.5 py-1 text-[10px] leading-tight text-amber-800">
                    <BellRing size={10} className="shrink-0" />
                    <span className="truncate"><span className="font-semibold">Follow-up</span> · {r.tfs_work_order_number}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DispatchBoard = ({ onSelect }) => {
  const { token } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [items, setItems] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [fieldUsers, setFieldUsers] = useState([]);
  const [providers, setProviders] = useState([]);
  const [tab, setTab] = useState('board');
  const [calMode, setCalMode] = useState('month');
  const [cursor, setCursor] = useState(new Date());
  const [assignWo, setAssignWo] = useState(null);
  const [scheduleWo, setScheduleWo] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/work-orders`, authHeaders).then((r) => setItems((r.data || []).filter((w) => !CLOSED.includes(w.status)))).catch(() => {});
  }, [token]);
  useEffect(() => {
    load();
    axios.get(`${API}/work-orders/field-users`, authHeaders).then((r) => setFieldUsers(r.data || [])).catch(() => {});
    axios.get(`${API}/service-providers?active_only=true&qualified_only=true`, authHeaders).then((r) => setProviders(r.data || [])).catch(() => {});
    axios.get(`${API}/follow-up-reminders?status=active`, authHeaders).then((r) => setReminders(r.data || [])).catch(() => {});
  }, [load]);

  const doDispatch = async (wo) => {
    if (!wo.assigned_to && !wo.service_provider_id) { toast.error('Assign the work order before dispatching'); return; }
    try {
      await axios.post(`${API}/work-orders/${wo.id}/dispatch`, { dispatched_to: wo.assigned_to_name || null }, authHeaders);
      toast.success(`Dispatched ${wo.tfs_work_order_number}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not dispatch'); }
  };
  const doStart = async (wo) => {
    try {
      await axios.post(`${API}/work-orders/${wo.id}/status`, { status: 'In Progress' }, authHeaders);
      toast.success(`${wo.tfs_work_order_number} marked In Progress`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); }
  };

  const actions = {
    onOpen: onSelect,
    onAssign: (wo) => setAssignWo(wo),
    onSchedule: (wo) => setScheduleWo(wo),
    onDispatch: doDispatch,
    onStart: doStart,
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#8FC93A]">Operations</div>
          <h1 className="text-3xl font-extrabold text-[#0E2A47]">Dispatch &amp; Schedule</h1>
          <p className="text-sm text-slate-500 mt-1">Assign, schedule and dispatch active work orders to internal crews or providers.</p>
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-1">
          <button onClick={() => setTab('board')} data-testid="view-toggle-board"
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${tab === 'board' ? 'bg-[#0E2A47] text-white' : 'text-slate-600 hover:bg-gray-50'}`}>
            <LayoutGrid size={15} /> Board
          </button>
          <button onClick={() => setTab('calendar')} data-testid="view-toggle-calendar"
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${tab === 'calendar' ? 'bg-[#0E2A47] text-white' : 'text-slate-600 hover:bg-gray-50'}`}>
            <CalendarDays size={15} /> Calendar
          </button>
        </div>
      </div>

      {tab === 'board' && <BoardView items={items} actions={actions} />}

      {tab === 'calendar' && (
        <div>
          <div className="flex gap-1 mb-3">
            <button onClick={() => setCalMode('month')} data-testid="cal-mode-month"
              className={`rounded-md px-3 py-1 text-xs font-medium border ${calMode === 'month' ? 'bg-[#0E2A47] text-white border-[#0E2A47]' : 'bg-white text-slate-600 border-gray-200'}`}>Month</button>
            <button onClick={() => setCalMode('week')} data-testid="cal-mode-week"
              className={`rounded-md px-3 py-1 text-xs font-medium border ${calMode === 'week' ? 'bg-[#0E2A47] text-white border-[#0E2A47]' : 'bg-white text-slate-600 border-gray-200'}`}>Week</button>
          </div>
          <CalendarView items={items} reminders={reminders} mode={calMode} cursor={cursor} setCursor={setCursor} onOpen={onSelect} onOpenReminder={(r) => onSelect(r.work_order_id)} />
          <p className="mt-3 text-xs text-slate-400">Scheduled work orders and amber follow-up reminders appear on the calendar. Click any item to open it.</p>
        </div>
      )}

      {assignWo && <AssignDialog wo={assignWo} fieldUsers={fieldUsers} providers={providers} onClose={() => setAssignWo(null)} onDone={load} />}
      {scheduleWo && <ScheduleDialog wo={scheduleWo} onClose={() => setScheduleWo(null)} onDone={load} />}
    </div>
  );
};

export default DispatchBoard;
